/** The `fields` area: the field, note and description editors, a flow's field
 *  and rule editors, and push rules. Owned by the stage 2 slot components-b
 *  (docs/I18N.md §11). */
export const fields = {
  // --- The description and the note (description-field.tsx, note-field.tsx) --
  'fields.description.label': 'Description',
  'fields.description.placeholder': 'Add a description…',
  'fields.description.edit': 'Edit description',
  'fields.note.label': 'Note',
  'fields.note.placeholder': 'Add a note…',
  'fields.note.edit': 'Edit note',

  // --- Rendered prose (markdown.tsx): a task item's read-only box ------------
  'fields.markdown.taskChecked': 'Checked',
  'fields.markdown.taskOpen': 'Not checked',

  // --- A set of people or groups as a value (entity-set-field.tsx) ------------
  'fields.entitySet.search': 'Search people…',
  'fields.entitySet.empty': 'No one to add.',
  'fields.entitySet.remove': 'Remove {name}',

  // --- The tags on a subject (tag-set-field.tsx) ------------------------------
  'fields.tags.tag': 'Tag',
  'fields.tags.add': 'Add a tag',
  'fields.tags.edit': 'Edit #{tag}',
  'fields.tags.path': 'Tag path',
  'fields.tags.remove': 'Remove',
  'fields.tags.removeTag': 'Remove #{tag}',

  // --- The grain-aware time input (time-value-input.tsx) ----------------------
  'fields.time.grain.day': 'Day',
  'fields.time.grain.week': 'Week',
  'fields.time.grain.month': 'Month',
  'fields.time.grain.quarter': 'Quarter',
  'fields.time.grain.year': 'Year',
  'fields.time.grain.other': 'Other',
  /** `label`: the field's own name. */
  'fields.time.precision': '{label}: precision',
  'fields.time.year': '{label}: year',
  'fields.time.quarter': '{label}: quarter',
  'fields.time.quarterNone': 'Q…',
  'fields.time.quarterOption': 'Q{n}',
  /** Examples of the canonical time grammar, which no language changes. */
  'fields.time.weekExample': '2026-W24',
  'fields.time.rangeExample': '2026-Q1/2026-Q3',
  'fields.time.otherHint':
    'Any canonical time: a datetime, or a start/end range like 2026-Q1/2026-Q3',

  // --- Push rules: who may seat you directly (push-rule-editor.tsx) -----------
  'fields.pushRule.myGroups': 'My groups',
  'fields.pushRule.onlyThese': 'Only these',
  'fields.pushRule.askMe': 'Ask me',
  'fields.pushRule.members': 'Members',
  'fields.pushRule.ask': 'Ask',
  'fields.pushRule.label': 'Who may assign directly',
  'fields.pushRule.remove': 'Remove {name} from the circle',
  'fields.pushRule.add': 'Add to the circle…',
  'fields.pushRule.empty': 'No one to add.',

  // --- Fact rules: a threshold as a sentence (fact-rules-editor.tsx) ----------
  /** What firing does: state the word, or ask the flow's people. */
  'fields.factRules.mode.declare': 'call it',
  'fields.factRules.mode.propose': 'suggest calling it',
  'fields.factRules.always': 'always',
  /** `attr` and `value`: an attribute's label and one of its values. */
  'fields.factRules.while': 'while {attr}: {value}',
  'fields.factRules.needsDays': 'How long may "{fact}" run before it matters? Nothing here knows.',
  'fields.factRules.needsWord': 'A grade needs a word: what does this flow call it?',
  /** A condition rule (docs/THRESHOLDS.md §2): the tag or the stage it names. */
  'fields.factRules.needsTag': 'Which tag? Pick the tag this rule names.',
  'fields.factRules.needsStage': 'Which stage? Pick a stage of this flow.',
  'fields.factRules.stage': 'Stage',
  'fields.factRules.pickStage': 'Pick a stage',
  /** Between a condition and its grade: "#legal = warning". */
  'fields.factRules.equals': '=',
  'fields.factRules.fact': 'Fact',
  'fields.factRules.past': 'past',
  'fields.factRules.days': 'Days',
  'fields.factRules.daysEquals': 'days =',
  'fields.factRules.grade': 'Grade',
  'fields.factRules.grade.notice': 'notice',
  'fields.factRules.grade.warning': 'warning',
  'fields.factRules.grade.critical': 'critical',
  'fields.factRules.firing': 'What firing does',
  'fields.factRules.word': 'Word',
  'fields.factRules.wordPlaceholder': 'Stalled',
  'fields.factRules.whileCondition': 'While condition',
  'fields.factRules.remove': 'Remove rule {fact} = {grade}',
  'fields.factRules.add': '+ Add rule',

  // --- Authored field definitions (field-defs-editor.tsx) ---------------------
  'fields.defs.type.text': 'Text',
  'fields.defs.type.prose': 'Long text (markdown)',
  'fields.defs.type.number': 'Number',
  'fields.defs.type.date': 'Date',
  'fields.defs.type.selection': 'Selection',
  'fields.defs.type.actor': 'Person or group',
  /** The rest-state type word, lowercase in English. */
  'fields.defs.typeWord.text': 'text',
  'fields.defs.typeWord.prose': 'long text',
  'fields.defs.typeWord.number': 'number',
  'fields.defs.typeWord.date': 'date',
  'fields.defs.typeWord.selection': 'selection',
  'fields.defs.typeWord.actor': 'person or group',
  'fields.defs.typeWordMany': '{type} · multiple',
  'fields.defs.problem.name': 'Name this field.',
  'fields.defs.problem.description': 'Say what this field means.',
  'fields.defs.problem.values': 'A selection needs at least one value.',
  'fields.defs.about': 'About fields',
  'fields.defs.aboutText':
    'A field needs a description: say what it means, not just what it stores. The same words describe it to every future reader, human or agent.',
  'fields.defs.minted': 'minted on save',
  'fields.defs.remove': 'Remove field {name}',
  'fields.defs.edit': 'Edit field {name}',
  /** `refs`: what already points at the field, as the host phrased it. */
  'fields.defs.inUseConfirm': 'In use: {refs}. Remove anyway?',
  'fields.defs.inUse': 'In use: {refs}',
  'fields.defs.removeConfirm': 'Remove',
  'fields.defs.keep': 'Keep it',
  'fields.defs.untitled': 'Untitled',
  'fields.defs.onCards': 'on cards',
  'fields.defs.name': 'Name',
  'fields.defs.namePlaceholder': 'e.g. Priority',
  'fields.defs.nameLabel': 'Field name',
  'fields.defs.type': 'Type',
  'fields.defs.typeLabel': 'Field type',
  'fields.defs.holds': 'Holds',
  'fields.defs.holdsLabel': 'How many values this field holds',
  'fields.defs.holdsOne': 'One',
  'fields.defs.holdsMany': 'Multiple',
  'fields.defs.cards': 'Cards',
  'fields.defs.showOnCardsHint': "Member cards show this field's value (cards show at most three)",
  'fields.defs.showOnCards': 'Show on cards',
  'fields.defs.description': 'Description',
  'fields.defs.descriptionLabel': 'Field description',
  'fields.defs.values': 'Values',
  'fields.defs.valuesHint': 'Enter adds a value; click a value to describe it.',
  'fields.defs.valuesLabel': 'Selection values',
  'fields.defs.valuesPlaceholder': 'Add a value',
  'fields.defs.discards': {
    one: 'Saving as {type} discards {count} value(s); switch back to Selection to keep them.',
    other: 'Saving as {type} discards {count} value(s); switch back to Selection to keep them.',
  },
  'fields.defs.done': 'OK',
  'fields.defs.add': '+ Add field',
} as const;
